import { useEffect, useState } from "react";
import { useStore } from "./stores/store";
import { TopBar } from "./components/shell/TopBar";
import { Board } from "./components/board/Board";
import { BoardTabs } from "./components/board/BoardSwitcher";
import { CardModal } from "./components/board/CardModal";
import { VoiceRecorder } from "./components/voice/VoiceRecorder";
import { StatsBar } from "./components/shell/StatsBar";
import { ChatPanel } from "./components/chat/ChatPanel";
import { SettingsModal } from "./components/settings/SettingsModal";
import { ProactiveBanner } from "./components/proactive/ProactiveBanner";
import { DigestModal } from "./components/digest/DigestModal";
import { DialogHost } from "./components/shell/DialogHost";
import { Login } from "./components/auth/Login";
import { OnboardingGroq } from "./components/onboarding/OnboardingGroq";
import IagoTour from "./components/iago/IagoTour";
import IagoFloater from "./components/iago/IagoFloater";
import { promptAsync } from "./stores/dialogStore";
import { supabase } from "./lib/supabase";
import type { Session } from "@supabase/supabase-js";

export default function App() {
  const load = useStore((s) => s.load);
  const loading = useStore((s) => s.loading);
  const cards = useStore((s) => s.cards);
  const activeCardId = useStore((s) => s.activeCardId);
  const setActiveCard = useStore((s) => s.setActiveCard);
  const lanes = useStore((s) => s.lanes);
  const toggleChat = useStore((s) => s.toggleChat);
  const chatOpen = useStore((s) => s.chatOpen);

  const onboardingOpen = useStore((s) => s.onboardingOpen);
  const setOnboardingOpen = useStore((s) => s.setOnboardingOpen);
  const loadSettings = useStore((s) => s.loadSettings);
  const userSettings = useStore((s) => s.userSettings);
  const markTutorialCompleted = useStore((s) => s.markTutorialCompleted);
  const setUserEmail = useStore((s) => s.setUserEmail);
  const resetOnSignOut = useStore((s) => s.resetOnSignOut);

  const tourOpen = useStore((s) => s.tourOpen);
  const setTourOpen = useStore((s) => s.setTourOpen);

  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);

  const activeCard = cards.find((c) => c.id === activeCardId) || null;

  // Auth lifecycle
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUserEmail(data.session?.user?.email ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      setUserEmail(newSession?.user?.email ?? null);
      if (event === "SIGNED_OUT") {
        resetOnSignOut();
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [setUserEmail, resetOnSignOut]);

  // Carrega board + settings quando logado
  useEffect(() => {
    if (!session) return;
    (async () => {
      await loadSettings();
      await load();
      const st = useStore.getState().userSettings;
      if (!st.tutorialCompleted) {
        setTourOpen(true);
      } else if (!st.hasGroqKey) {
        setOnboardingOpen(true);
      }
    })();
  }, [session, load, loadSettings, setOnboardingOpen]);

  async function finishTour(openGroq: boolean) {
    setTourOpen(false);
    try {
      await markTutorialCompleted();
    } catch {}
    const st = useStore.getState().userSettings;
    if (openGroq && !st.hasGroqKey) {
      setOnboardingOpen(true);
    }
  }

  useEffect(() => {
    if (!session) return;
    function onKey(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      const typing = ["INPUT", "TEXTAREA"].includes(target?.tagName) || target?.isContentEditable;

      if ((e.metaKey || e.ctrlKey) && e.key === "n") {
        e.preventDefault();
        if (!userSettings.hasGroqKey) {
          setOnboardingOpen(true);
          return;
        }
        setRecorderOpen(true);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('input[placeholder^="Buscar"]')?.focus();
      }
      if ((e.metaKey || e.ctrlKey) && e.key === "/") {
        e.preventDefault();
        toggleChat();
      }
      if (e.key === "/" && !typing) {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('input[placeholder^="Buscar"]')?.focus();
      }
      if (e.key === "n" && !typing && !recorderOpen && !activeCardId && !chatOpen) {
        if (!userSettings.hasGroqKey) {
          setOnboardingOpen(true);
          return;
        }
        setRecorderOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [session, activeCardId, recorderOpen, chatOpen, toggleChat, userSettings.hasGroqKey, setOnboardingOpen]);

  async function onNewLane() {
    const title = await promptAsync({
      title: "Nova raia",
      placeholder: "Ex: Revisão, Espera, Arquivado…",
      confirmLabel: "Criar",
    });
    if (!title?.trim()) return;
    await useStore.getState().createLane(title.trim());
  }

  // Loading de sessão inicial
  if (session === undefined) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="font-mono text-xs text-slate-500 animate-pulse">carregando…</div>
      </div>
    );
  }

  if (!session) {
    return (
      <>
        <Login />
        <DialogHost />
      </>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      <TopBar
        onRecord={() => {
          if (!userSettings.hasGroqKey) return setOnboardingOpen(true);
          setRecorderOpen(true);
        }}
        onNewLane={onNewLane}
        onOpenDigest={() => setDigestOpen(true)}
      />
      <StatsBar />
      <ProactiveBanner />
      <div className={chatOpen ? "flex flex-1 overflow-hidden md:pr-[400px]" : "flex flex-1 overflow-hidden"}>
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="animate-pulse font-mono text-sm text-slate-400">Organizando tudo aqui.</div>
          </div>
        ) : lanes.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-400">
            Acordando aqui, um segundo.
          </div>
        ) : (
          <Board onCardClick={(id) => setActiveCard(id)} />
        )}
      </div>
      <BoardTabs />

      <CardModal card={activeCard} onClose={() => setActiveCard(null)} />
      <VoiceRecorder
        open={recorderOpen}
        onClose={() => setRecorderOpen(false)}
        defaultLaneId={lanes[0]?.id ?? null}
      />
      <ChatPanel />
      <SettingsModal />
      <DigestModal open={digestOpen} onClose={() => setDigestOpen(false)} />
      <OnboardingGroq
        open={onboardingOpen}
        onClose={() => setOnboardingOpen(false)}
        canSkip={true}
      />
      <IagoTour
        open={tourOpen}
        onComplete={() => finishTour(true)}
        onSkip={() => finishTour(false)}
      />
      {!tourOpen && userSettings.tutorialCompleted && <IagoFloater />}
      <DialogHost />
    </div>
  );
}
